import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

const Aside = styled.aside`
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 50;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e2e8f0; /* slate-200 */
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  transition: width 0.3s ease;
  width: ${(p) => (p.$open ? '256px' : '80px')};
`;

const TopBar = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 16px;
`;

const BrandBadge = styled.div`
  height: 32px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #0ea5e9; /* sky-500 */
  color: #fff;
  font-weight: 700;
  font-size: 12px;
`;

const Nav = styled.nav`
  flex: 1;
  padding: 16px 12px;
  overflow-y: auto;
`;

const NavButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: none;
  background: ${(p) => (p.$active ? '#e2f3ff' : 'transparent')};
  color: ${(p) => (p.$active ? '#0369a1' : '#334155')};
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
`;

const NavLink = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 8px;
  padding: 10px 12px;
  color: ${(p) => (p.$active ? '#fff' : '#334155')};
  background: ${(p) => (p.$active ? '#0ea5e9' : 'transparent')};
  text-decoration: none;
  font-size: 14px;
`;

const ChildWrap = styled.div`
  margin-top: 4px;
  padding-left: 24px;
  display: ${(p) => (p.$open ? 'block' : 'none')};
`;

const Footer = styled.div`
  border-top: 1px solid #e2e8f0;
  padding: 16px;
  color: #64748b; /* slate-500 */
  font-size: 12px;
`;

const IconBase = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  />
);

const DashboardIcon = () => (
  <IconBase>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="4" rx="1" />
    <rect x="14" y="10" width="7" height="11" rx="1" />
    <rect x="3" y="13" width="7" height="8" rx="1" />
  </IconBase>
);

const InquiryIcon = () => (
  <IconBase>
    <path d="M5 4h14a1 1 0 0 1 1 1v11.5a1 1 0 0 1-1.53.85L12 14.5l-6.47 2.85A1 1 0 0 1 4 16.5V5a1 1 0 0 1 1-1Z" />
  </IconBase>
);

const TargetIcon = () => (
  <IconBase>
    <circle cx="12" cy="12" r="7" />
    <circle cx="12" cy="12" r="3" />
    <path d="M12 3v2" />
    <path d="M21 12h-2" />
    <path d="M12 21v-2" />
    <path d="M5 12H3" />
  </IconBase>
);

const CourseIcon = () => (
  <IconBase>
    <path d="M4 6.5 12 4l8 2.5-8 2.5L4 6.5Z" />
    <path d="M4 11l8 2.5 8-2.5" />
    <path d="M4 15.5 12 18l8-2.5" />
  </IconBase>
);

const WorkIcon = () => (
  <IconBase>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </IconBase>
);

const OfferIcon = () => (
  <IconBase>
    <path d="M7 4h10a1 1 0 0 1 1 1v13l-6-3-6 3V5a1 1 0 0 1 1-1Z" />
  </IconBase>
);

const PerformanceIcon = () => (
  <IconBase>
    <path d="M4 19V9" />
    <path d="M10 19V5" />
    <path d="M16 19v-8" />
    <path d="M3 19h18" />
  </IconBase>
);

const ProfileIcon = () => (
  <IconBase>
    <circle cx="12" cy="9" r="3.2" />
    <path d="M6.5 19a5.5 5.5 0 0 1 11 0" />
  </IconBase>
);

export default function SalesSidebar({ isOpen, currentSection }) {
  const [expanded, setExpanded] = useState([]);
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  const navigation = useMemo(
    () => ([
      { name: 'Dashboard', key: 'dashboard', href: '/sales/dashboard', icon: <DashboardIcon /> },
      { name: 'Inquiry', key: 'inquiry', href: '/sales/inquiry', icon: <InquiryIcon /> },
      { name: 'Target', key: 'target', href: '/sales/target', icon: <TargetIcon /> },
      { name: 'Course info', key: 'course-info', href: '/sales/course-info', icon: <CourseIcon /> },
      { name: 'My work', key: 'my-work', href: '/sales/my-work', icon: <WorkIcon /> },
      { name: 'Offer letter', key: 'offer-letter', href: '/sales/offer-letter', icon: <OfferIcon /> },
      { name: 'Performance', key: 'performance', href: '/sales/performance', icon: <PerformanceIcon /> },
      { name: 'Profile', key: 'profile', href: '/sales/profile', icon: <ProfileIcon /> },
    ]),
    []
  );

  const isActive = (href) => {
    if (!href || href === '#') return false;
    if (href === '/sales/dashboard') return currentPath === href || currentSection === 'dashboard';
    if (href === '/sales/inquiry') return currentPath === href || currentSection === 'inquiry';
    if (href === '/sales/target') return currentPath === href || currentSection === 'target';
    if (href === '/sales/course-info') return currentPath === href || currentSection === 'course-info';
    if (href === '/sales/my-work') return currentPath === href || currentSection === 'my-work';
    if (href === '/sales/offer-letter') return currentPath === href || currentSection === 'offer-letter';
    if (href === '/sales/performance') return currentPath === href || currentSection === 'performance';
    if (href === '/sales/profile') return currentPath === href || currentSection === 'profile';
    return currentPath.startsWith(href);
  };

  const toggleExpanded = (name) => {
    setExpanded((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };

  return (
    <Aside $open={isOpen}>
      <TopBar>
        {isOpen ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BrandBadge>SL</BrandBadge>
            <span style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>Sales</span>
          </div>
        ) : (
          <BrandBadge>SL</BrandBadge>
        )}
      </TopBar>
      <Nav>
        {navigation.map((item) => {
          const itemActive = isActive(item.href);
          return (
            <div key={item.key || item.name} style={{ marginBottom: 6 }}>
              <NavLink href={item.href} $active={itemActive}>
                <span>{item.icon}</span>
                {isOpen && <span>{item.name}</span>}
              </NavLink>
            </div>
          );
        })}
      </Nav>
      {isOpen && (
        <Footer>
          <div>
            <div style={{ fontWeight: 600 }}>Sales Suite</div>
            <div style={{ marginTop: 4 }}>Version 1.0.0</div>
          </div>
        </Footer>
      )}
    </Aside>
  );
}
